import { values } from './values';
import { porterStem } from './porter-stemmer';
import { AUTHORING_TOOL_KEYS } from '../constants/AuthoringToolLabels';

const userLocale = navigator.language || 'en-US';

/**
 * Base tools with generic utilities only - NO service or API imports
 * This prevents circular dependencies and allows safe import from API services
 * For service-dependent utilities, use tools.js instead
 */
export const toolsBase = {

    keyClick(handler) {
        if (!handler) return null;
        return (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handler(e);
            }
        };
    },

    debounce(fn, delay) {
        let timer
        return function (...args) {
            clearTimeout(timer)
            timer = setTimeout(() => fn.apply(this, args), delay)
        }
    },

    encodeObjectToBase64(obj) {
        return btoa(JSON.stringify(obj))
    },
    decodeBase64ToObject(str) {
        return JSON.parse(atob(str))
    },

    getGreeting() {
        const now = new Date();
        const hour = now.getHours();

        if (hour < 12) {
            return window.locale?.t('goodmorning') || "Good morning";
        } else if (hour < 18) {
            return window.locale?.t('goodafternoon') || "Good afternoon";
        } else {
            return window.locale?.t('goodevening') || "Good evening";
        }
    },

    mediaTypeDisplay(type) {
        const typeMap = {
            "scorm-1.2": "mediatype_scorm12",
            "scorm-2004": "mediatype_scorm2004",
            "xapi": "mediatype_xapi",
            "cmi5": "mediatype_cmi5",
            "aicc": "mediatype_aicc",
            "video": "mediatype_video",
            "audio": "mediatype_audio",
            "image": "mediatype_image",
            "pdf": "mediatype_pdf",
            "epub": "mediatype_epub",
            "word": "mediatype_word",
            "powerpoint": "mediatype_powerpoint",
            "html": "mediatype_html",
            "rtf": "mediatype_rtf",
            "zip": "mediatype_zip",
            "vtt": "mediatype_vtt",
            "text": "mediatype_text",
            "markdown": "mediatype_markdown"
        };

        const key = typeMap[type];
        return key ? (window.locale?.t(key) || type) : type;
    },

    authoringToolDisplay(type) {
        const key = AUTHORING_TOOL_KEYS[type] || "authoringtool_unknown";
        return window.locale?.t(key) || "Unknown";
    },

    getAuthoringToolsFilterOptions() {
        return (values.authoringTools || [])
            .filter(tool => tool !== 'generic' && tool !== 'easyg')
            .sort((a, b) => a.localeCompare(b));
    },

    searchTypeDisplay(type) {
        const typeMap = {
            "semantic": "searchtype_semantic",
            "full-text": "searchtype_fulltext",
            "metadata": "searchtype_metadata"
        };

        const key = typeMap[type];
        return key ? (window.locale?.t(key) || "") : "";
    },

    filterChunksBySearchType(chunks, searchTypes) {
        if (!Array.isArray(chunks) || !Array.isArray(searchTypes)) {
            return [];
        }

        if (searchTypes.length === 0) {
            return chunks;
        }

        return chunks.filter(chunk => {
            if (!chunk.searchType || !Array.isArray(chunk.searchType)) {
                return false;
            }

            return chunk.searchType.some(type => searchTypes.includes(type));
        });
    },

    resourceStatusDisplay(status) {
        const map = {
            "Active": "resourcestatus_active",
            "Inactive": "resourcestatus_inactive",
            "Archived": "resourcestatus_archived",
            "Deleted": "resourcestatus_deleted"
        };
        const key = map[status];
        return key ? (window.locale?.t(key) || status) : (status || "");
    },

    resourceActionDisplay(action) {
        const map = {
            "Retain": "resourceaction_retain",
            "Refresh": "resourceaction_refresh",
            "Retire": "resourceaction_retire",
            "Reuse": "resourceaction_reuse",
            "Review": "resourceaction_review"
        };
        const key = map[action];
        return key ? (window.locale?.t(key) || action) : (action || "");
    },

    skillOriginDisplay(origin) {
        if (!origin) return "";
        if (origin === 'custom') return window.locale?.t('skilltable_origin_custom') || 'Custom';
        return origin;
    },

    fileSizeDisplay(bytes, decimals = 1) {
        if (!bytes) return '';
        const k = 1024;
        const sizeKeys = ['filesize_bytes', 'filesize_kb', 'filesize_mb', 'filesize_gb', 'filesize_tb', 'filesize_pb', 'filesize_eb'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
        const sizeLabel = window.locale?.t(sizeKeys[i]) || ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'][i];
        return `${this.formatNumber(size, 0)} ${sizeLabel}`;
    },

    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0;
            var v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    },

    getUserLocale() {
        return userLocale;
    },

    formatDate(date, options) {
        if (!date) return "";
        return new Date(date).toLocaleDateString(userLocale, options);
    },

    formatDateTime(date, options) {
        if (!date) return "";
        return new Date(date).toLocaleString(userLocale, options);
    },

    formatTime(date, options) {
        if (!date) return "";
        return new Date(date).toLocaleTimeString(userLocale, options);
    },

    downloadBlob(blob, filename) {
        const url = URL.createObjectURL(blob);
        this.downloadFile(url, filename);
        URL.revokeObjectURL(url);
    },

    downloadFile(url, filename) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },

    getItemStatusColor(text) {
        switch (text) {
            case 'Active':
            case 'Retain':
                return 'text-lime-700';
            case 'Inactive':
                return 'text-sky-700';
            case 'Archived':
                return 'text-amber-700';
            case 'Deleted':
            case 'Retire':
                return 'text-red-700';
            case 'Refresh':
                return 'text-green-700';
            case 'Reuse':
                return 'text-indigo-700';
            case 'Review':
                return 'text-orange-700';
        }
    },

    isValidFileType(file) {
        if (!file || !file.name) return false;
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        return values.acceptedFiles.includes(ext);
    },

    isValidJson(value) {
        return typeof value === "object" && value !== null;
    },

    isValidJsonString(str) {
        if (typeof str !== "string") return false;

        try {
            const parsed = JSON.parse(str);
            return typeof parsed === "object" && parsed !== null;
        } catch {
            return false;
        }
    },

    formatExtractChunk(chunk, searchTerm) {
        let text = chunk.text.replace(/\n/g, '<br>');
        text = this.highlightKeywords(text, searchTerm);
        return text;
    },

    highlightKeywords(text, searchTerm) {
        if (!text) text = "";
        if (!searchTerm || typeof searchTerm !== "string") return text;

        // Preserve outer quotes for exact match detection, then remove them
        const trimmedTerm = searchTerm.trim();
        const isExactMatch = /^".*"$/.test(trimmedTerm);

        // Remove outer quotes if present
        let processedTerm = isExactMatch
            ? trimmedTerm.slice(1, -1).trim()
            : trimmedTerm;

        // Remove ALL non-alphanumeric and non-whitespace characters (mimics API preprocessing)
        processedTerm = processedTerm.replace(/[^a-zA-Z0-9\s]/g, '').trim();
        if (!processedTerm) return text;

        // Creates pattern that matches text while ignoring punctuation between characters
        // "OK" matches "OK", "O.K.", "O-K" but not "OxK"
        const createPattern = (str, useWordBoundaries = false) => {
            let pattern = '';
            for (let i = 0; i < str.length; i++) {
                const char = str[i];
                if (/[a-zA-Z0-9]/.test(char)) {
                    pattern += char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    if (i < str.length - 1 && /[a-zA-Z0-9]/.test(str[i + 1])) {
                        pattern += '[^a-zA-Z0-9\\s]*';
                    }
                } else if (/\s/.test(char)) {
                    pattern += '[^a-zA-Z0-9]*\\s+[^a-zA-Z0-9]*';
                }
            }
            return useWordBoundaries ? '\\b' + pattern + '\\b' : pattern;
        };

        // Creates a stem-based pattern for a word that matches morphological variants
        // porterStem("parties") = "parti" → matches "party", "parties", "partied"
        // Also includes simple suffix pattern as a fallback layer
        const createStemPattern = (word) => {
            const stem = porterStem(word);
            const escaped = stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Match stem + any word-character suffix (captures plurals, tenses, etc.)
            return escaped + '\\w*';
        };

        // Creates a stem-based phrase pattern where each word can match its variants
        // "sample text" → stem pattern matching "samples texts", "sampled texting", etc.
        const createStemPhrasePattern = (phrase) => {
            const words = phrase.split(/\s+/).filter(w => w.length > 0);
            return words.map(w => createStemPattern(w)).join('[^a-zA-Z0-9]*\\s+[^a-zA-Z0-9]*');
        };

        if (isExactMatch) {
            // Exact phrase: highlight both the literal phrase AND stem-based variants
            const exactPattern = createPattern(processedTerm, true);
            const stemPattern = '\\b' + createStemPhrasePattern(processedTerm) + '\\b';

            // Combine both patterns — exact first, then stem variants
            const combined = `(${exactPattern})|(${stemPattern})`;
            const regex = new RegExp(combined, 'gi');
            text = text.replace(regex, '<span class="highlight">$&</span>');
        } else {
            // Individual word matching with stem-based variant support
            const words = processedTerm
                .split(/\s+/)
                .filter(w => w.length > 2);

            if (words.length > 0) {
                const patterns = words.map(word => {
                    const exactPattern = createPattern(word, true);
                    const stemPattern = '\\b' + createStemPattern(word) + '\\b';
                    return `${exactPattern}|${stemPattern}`;
                });
                const regex = new RegExp(`(${patterns.join('|')})`, 'gi');
                text = text.replace(regex, '<span class="highlight">$1</span>');
            }
        }

        return text;
    },

    formatExtractChunkScore(score) {
        return score.toFixed(3);
    },

    // Sort a resource's associated-collections array alphabetically by display
    // name. Entries can be { collection: { displayName } } (the join shape from
    // the resource API) or { displayName } (flat shape). Comparison is
    // case-insensitive and locale-aware via localeCompare with sensitivity:
    // 'base', so diacritic names sort in their natural alphabetical position.
    // Returns a new array; the input is not mutated.
    sortCollectionsByName(collections) {
        return [...(collections || [])].sort((a, b) => {
            const an = (a?.collection?.displayName || a?.displayName || '').toString();
            const bn = (b?.collection?.displayName || b?.displayName || '').toString();
            return an.localeCompare(bn, undefined, { sensitivity: 'base' });
        });
    },

    formatNumber(value, decimals = 0) {
        if (isNaN(value) || value === null) return '';
        return Number(value)
            .toLocaleString(userLocale, {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });
    },

    formatCurrency(amount, currency, showCents = true) {
        if (isNaN(amount) || amount === null) return '';
        return Number(amount).toLocaleString(userLocale, {
            style: 'currency',
            currency: currency || 'USD',
            minimumFractionDigits: showCents ? 2 : 0,
            maximumFractionDigits: showCents ? 2 : 0
        });
    },

    getResourceAttribute(resource, key) {
        const existingAttr = resource.attributes?.find(attr => attr.key === key);

        if (existingAttr) {
            return existingAttr.value;
        } else {
            return null;
        }
    }
};
